/**
 * WebRTC泄漏检测工具
 * 用于检测WebRTC是否泄露真实IP地址
 */

export default class WebRTCLeakDetector {
  constructor() {
    this.isDetecting = false;
    this.detectedIPs = {
      public: [],
      private: [],
      host: []
    };
  }

  /**
   * 开始检测WebRTC泄漏
   * @returns {Promise} 返回包含检测结果的Promise
   */
  detectLeaks() {
    return new Promise((resolve, reject) => {
      if (this.isDetecting) {
        reject(new Error('已经在进行检测'));
        return;
      }

      this.isDetecting = true;
      this.resetDetectedIPs();

      // 检查是否支持WebRTC
      if (!window.RTCPeerConnection) {
        this.isDetecting = false;
        resolve({
          leakDetected: false,
          webRTCSupported: false,
          detectedIPs: this.detectedIPs
        });
        return;
      }

      // 设置超时，确保我们不会无限期等待
      const timeout = setTimeout(() => {
        this.isDetecting = false;
        resolve({
          leakDetected: this.hasDetectedIPs(),
          webRTCSupported: true,
          detectedIPs: this.detectedIPs,
          timedOut: true
        });
      }, 5000);

      try {
        // 使用RTCPeerConnection检测IP
        const pc = new RTCPeerConnection({ 
          iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
        });

        // 当发现新的候选者(包含IP地址)时触发
        pc.onicecandidate = (event) => {
          if (event.candidate) {
            this.handleCandidateIP(event.candidate);
          }
        };

        // 添加一个数据通道触发ICE收集
        pc.createDataChannel('webrtc-leak-test');

        // 创建offer以启动ICE收集
        pc.createOffer()
          .then(offer => pc.setLocalDescription(offer))
          .catch(err => {
            clearTimeout(timeout);
            this.isDetecting = false;
            reject(err);
          });

        // ICE收集完成时
        pc.onicegatheringstatechange = () => {
          if (pc.iceGatheringState === 'complete') {
            clearTimeout(timeout);
            this.isDetecting = false;

            // 延迟一下以确保所有候选者都已处理
            setTimeout(() => {
              resolve({
                leakDetected: this.hasDetectedIPs(),
                webRTCSupported: true,
                detectedIPs: this.detectedIPs
              });

              // 关闭连接
              pc.close();
            }, 500);
          }
        };
      } catch (error) {
        clearTimeout(timeout);
        this.isDetecting = false;
        reject(error);
      }
    });
  }

  /**
   * 从ICE候选者中提取IP地址
   * @param {RTCIceCandidate} candidate ICE候选者
   */
  handleCandidateIP(candidate) {
    const ip = this.extractIP(candidate.candidate);
    if (ip) {
      if (this.isPrivateIP(ip)) {
        if (!this.detectedIPs.private.includes(ip)) {
          this.detectedIPs.private.push(ip);
        }
      } else if (this.isHostIP(candidate.candidate)) {
        if (!this.detectedIPs.host.includes(ip)) {
          this.detectedIPs.host.push(ip);
        }
      } else {
        if (!this.detectedIPs.public.includes(ip)) {
          this.detectedIPs.public.push(ip);
        }
      }
    }
  }

  /**
   * 从ICE候选者字符串中提取IP地址
   * @param {string} candidateStr ICE候选者字符串
   * @returns {string|null} IP地址或null
   */
  extractIP(candidateStr) {
    const ipRegex = /([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-f0-9]{1,4}(:[a-f0-9]{1,4}){7})/;
    const match = ipRegex.exec(candidateStr);
    return match ? match[1] : null;
  }

  /**
   * 检查是否是私有IP地址
   * @param {string} ip IP地址
   * @returns {boolean} 是否是私有IP
   */
  isPrivateIP(ip) {
    if (ip.startsWith('10.') || 
        ip.startsWith('192.168.') || 
        ip.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./)) {
      return true;
    }
    // IPv6本地地址检查
    if (ip.startsWith('fd') || ip.startsWith('fe80:')) {
      return true;
    }
    return false;
  }

  /**
   * 检查是否是主机候选者(vs服务器反射候选者)
   * @param {string} candidateStr 候选者字符串
   * @returns {boolean} 是否是主机候选者
   */
  isHostIP(candidateStr) {
    return candidateStr.indexOf('host') !== -1;
  }

  /**
   * 检查是否检测到任何IP地址
   * @returns {boolean} 是否检测到IP
   */
  hasDetectedIPs() {
    return this.detectedIPs.public.length > 0;
  }

  /**
   * 重置检测到的IP地址
   */
  resetDetectedIPs() {
    this.detectedIPs = {
      public: [],
      private: [],
      host: []
    };
  }
} 